import React, { useEffect, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Space,
  Typography,
  Tag,
  Empty,
  Tooltip,
  Alert,
  Spin,
  Badge,
  Divider,
  Select,
  Modal,
  message,
  Statistic
} from "antd";
import {
  SearchOutlined,
  BankOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  FilterOutlined,
  DownloadOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  ThunderboltOutlined,
  SafetyOutlined,
  LineChartOutlined,
  CloudSyncOutlined
} from "@ant-design/icons";
import { getRequest } from "../../../Utils/AppApi";
import debounce from "lodash/debounce";
import * as XLSX from "xlsx";

const { Text, Title } = Typography;
const { Option } = Select;

const InstantPayAEPSBanks = ({ history }) => {
  // State Management
  const [banks, setBanks] = useState([]);
  const [filteredBanks, setFilteredBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [viewMode, setViewMode] = useState("table");
  const [failureRateThreshold, setFailureRateThreshold] = useState(50);
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Refs
  const searchInputRef = useRef(null);
  const tableRef = useRef(null);

  // API Configuration
  const API_ENDPOINT = '/aeps/bank-list';
  const CACHE_KEY = 'aeps_banks_cache';
  const CACHE_TIMESTAMP_KEY = 'aeps_banks_timestamp';
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Check if cache is valid
   */
  const isCacheValid = () => {
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    if (!timestamp) return false;
    
    const age = Date.now() - parseInt(timestamp, 10);
    return age < CACHE_DURATION;
  };

  /**
   * Get cached data
   */
  const getCachedData = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error("Error reading cache:", error);
      return null;
    }
  };

  /**
   * Save data to cache
   */
  const saveToCache = (data) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error("Error saving to cache:", error);
    }
  };

  /**
   * Fetch banks data with caching
   */
  const fetchBanks = useCallback(async () => {
    setLoading(true);
    
    try {
      // Check cache first
      if (isCacheValid()) {
        const cachedData = getCachedData();
        if (cachedData) {
          setBanks(cachedData);
          setFilteredBanks(cachedData);
          setLoading(false);
          message.info("Loaded from cache");
          return;
        }
      }

      // Fetch from API
      const res = await getRequest(API_ENDPOINT, history);
      
      if (res?.success && Array.isArray(res.data)) {
        const banksData = res.data.map(bank => ({
          ...bank,
          aepsFailureRate: parseFloat(bank.aepsFailureRate) || 0,
          aadhaarpayFailureRate: parseFloat(bank.aadhaarpayFailureRate) || 0,
          performanceScore: 100 - ((parseFloat(bank.aepsFailureRate) || 0 + parseFloat(bank.aadhaarpayFailureRate) || 0) / 2)
        }));
        
        setBanks(banksData);
        setFilteredBanks(banksData);
        
        // Save to cache
        saveToCache(banksData);
        
        message.success(`Loaded ${banksData.length} banks from server`);
        setRefreshCounter(prev => prev + 1);
      } else {
        message.warning("No data received from server");
        setBanks([]);
        setFilteredBanks([]);
      }
    } catch (error) {
      console.error("Error fetching banks:", error);
      message.error("Network error. Loading from cache...");
      
      // Fallback to cache even if expired
      const cachedData = getCachedData();
      if (cachedData) {
        setBanks(cachedData);
        setFilteredBanks(cachedData);
      } else {
        setBanks([]);
        setFilteredBanks([]);
      }
    } finally {
      setLoading(false);
    }
  }, [history]);

  /**
   * Debounced search handler
   */
  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      if (!searchValue.trim()) {
        applyFilters(banks, filterStatus);
        return;
      }

      const searchLower = searchValue.toLowerCase();
      const filtered = banks.filter(bank => 
        bank.name?.toLowerCase().includes(searchLower) ||
        bank.iin?.toLowerCase().includes(searchLower) ||
        bank.bankId?.toString().toLowerCase().includes(searchLower)
      );

      applyFilters(filtered, filterStatus);
    }, 300),
    [banks, filterStatus]
  );

  /**
   * Apply multiple filters
   */
  const applyFilters = useCallback((data, status) => {
    let filtered = [...data];

    // Apply status filter
    if (status !== "all") {
      switch (status) {
        case "aeps_enabled":
          filtered = filtered.filter(b => b.aepsEnabled);
          break;
        case "aadhaarpay_enabled":
          filtered = filtered.filter(b => b.aadhaarpayEnabled);
          break;
        case "both_enabled":
          filtered = filtered.filter(b => b.aepsEnabled && b.aadhaarpayEnabled);
          break;
        case "high_failure":
          filtered = filtered.filter(b => 
            b.aepsFailureRate > failureRateThreshold || 
            b.aadhaarpayFailureRate > failureRateThreshold
          );
          break;
        case "low_failure":
          filtered = filtered.filter(b => 
            b.aepsFailureRate <= 20 && 
            b.aadhaarpayFailureRate <= 20
          );
          break;
        case "excellent":
          filtered = filtered.filter(b => 
            b.aepsFailureRate === 0 && 
            b.aadhaarpayFailureRate === 0
          );
          break;
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key.includes("FailureRate") || sortConfig.key === "performanceScore") {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredBanks(filtered);
  }, [sortConfig, failureRateThreshold]);

  /**
   * Handle sort
   */
  const handleSort = (column, sortDirection) => {
    setSortConfig({ key: column.selector, direction: sortDirection });
  };

  /**
   * Get failure rate color
   */
  const getFailureRateColor = (rate) => {
    const numRate = parseFloat(rate);
    if (numRate === 0) return "success";
    if (numRate <= 20) return "blue";
    if (numRate <= 40) return "orange";
    if (numRate <= 60) return "volcano";
    return "red";
  };

  /**
   * Get status icon
   */
  const getStatusIcon = (rate) => {
    const numRate = parseFloat(rate);
    if (numRate === 0) return <CheckCircleOutlined />;
    if (numRate <= 30) return <WarningOutlined />;
    return <CloseCircleOutlined />;
  };

  /**
   * Export to Excel
   */
  const exportToExcel = () => {
    if (filteredBanks.length === 0) {
      message.warning("No data to export");
      return;
    }

    try {
      const exportData = filteredBanks.map(bank => ({
        "Bank ID": bank.bankId,
        "Bank Name": bank.name,
        "IIN": bank.iin,
        "AEPS Enabled": bank.aepsEnabled ? "Yes" : "No",
        "AEPS Failure Rate": `${bank.aepsFailureRate}%`,
        "Aadhaar Pay Enabled": bank.aadhaarpayEnabled ? "Yes" : "No",
        "Aadhaar Pay Failure Rate": `${bank.aadhaarpayFailureRate}%`,
        "Performance Score": (100 - ((bank.aepsFailureRate + bank.aadhaarpayFailureRate) / 2)).toFixed(1)
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "AEPS Banks");
      
      // Auto-size columns
      const wscols = [
        { wch: 10 },
        { wch: 30 },
        { wch: 15 },
        { wch: 15 },
        { wch: 20 },
        { wch: 20 },
        { wch: 25 },
        { wch: 15 }
      ];
      ws['!cols'] = wscols;

      XLSX.writeFile(wb, `aeps_banks_${new Date().toISOString().split('T')[0]}.xlsx`);
      message.success("Exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export");
    }
  };

  /**
   * Show bank details modal
   */
  const showBankDetails = (bank) => {
    setSelectedBank(bank);
    setIsModalVisible(true);
  };

  /**
   * Clear cache
   */
  const clearCache = () => {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
    message.success("Cache cleared");
    fetchBanks(); // Refetch fresh data
  };

  /**
   * Calculate statistics
   */
  const calculateStats = useCallback(() => {
    if (banks.length === 0) {
      return {
        total: 0,
        aepsEnabled: 0,
        aadhaarpayEnabled: 0,
        highRisk: 0,
        lowRisk: 0
      };
    }

    const aepsEnabled = banks.filter(b => b.aepsEnabled).length;
    const aadhaarpayEnabled = banks.filter(b => b.aadhaarpayEnabled).length;
    const highRisk = banks.filter(b => 
      b.aepsFailureRate > failureRateThreshold || 
      b.aadhaarpayFailureRate > failureRateThreshold
    ).length;
    const lowRisk = banks.filter(b => 
      b.aepsFailureRate <= 20 && 
      b.aadhaarpayFailureRate <= 20
    ).length;
    const excellent = banks.filter(b => 
      b.aepsFailureRate === 0 && 
      b.aadhaarpayFailureRate === 0
    ).length;

    return {
      total: banks.length,
      aepsEnabled,
      aadhaarpayEnabled,
      highRisk,
      lowRisk,
      excellent,
      filtered: filteredBanks.length
    };
  }, [banks, filteredBanks, failureRateThreshold]);

  /**
   * Reset all filters
   */
  const resetFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setFailureRateThreshold(50);
    setSortConfig({ key: "name", direction: "asc" });
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
    message.info("Filters reset");
  };

  /**
   * Table columns configuration
   */
  const columns = [
    {
      name: "ID",
      selector: row => row.bankId,
      sortable: true,
      width: '90px',
      cell: row => (
        <Tooltip title={`Bank ID: ${row.bankId}`}>
          <Badge 
            count={row.bankId} 
            style={{ 
              backgroundColor: '#1890ff',
              fontSize: '11px',
              fontWeight: 'bold'
            }} 
          />
        </Tooltip>
      )
    },
    {
      name: "Bank Name",
      selector: row => row.name,
      sortable: true,
      cell: row => (
        <Space 
          direction="vertical" 
          size={2} 
          style={{ cursor: 'pointer' }} 
          onClick={() => showBankDetails(row)}
        >
          <Text strong style={{ fontSize: '14px' }}>{row.name}</Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            IIN: <Text code>{row.iin}</Text>
          </Text>
        </Space>
      )
    },
    {
      name: "AEPS",
      selector: row => row.aepsEnabled,
      sortable: true,
      width: '130px',
      cell: row => (
        <Space direction="vertical" size={4}>
          <Tag 
            color={row.aepsEnabled ? "success" : "error"} 
            icon={row.aepsEnabled ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            style={{ fontSize: '11px', margin: 0, cursor: 'pointer' }}
            onClick={() => showBankDetails(row)}
          >
            {row.aepsEnabled ? "Active" : "Inactive"}
          </Tag>
          {row.aepsEnabled && (
            <Tooltip title={`Failure Rate: ${row.aepsFailureRate}%`}>
              <Tag 
                color={getFailureRateColor(row.aepsFailureRate)}
                style={{ fontSize: '10px', margin: 0 }}
              >
                {getStatusIcon(row.aepsFailureRate)} {row.aepsFailureRate}%
              </Tag>
            </Tooltip>
          )}
        </Space>
      )
    },
    {
      name: "Aadhaar Pay",
      selector: row => row.aadhaarpayEnabled,
      sortable: true,
      width: '140px',
      cell: row => (
        <Space direction="vertical" size={4}>
          <Tag 
            color={row.aadhaarpayEnabled ? "success" : "error"} 
            icon={row.aadhaarpayEnabled ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            style={{ fontSize: '11px', margin: 0, cursor: 'pointer' }}
            onClick={() => showBankDetails(row)}
          >
            {row.aadhaarpayEnabled ? "Active" : "Inactive"}
          </Tag>
          {row.aadhaarpayEnabled && (
            <Tooltip title={`Failure Rate: ${row.aadhaarpayFailureRate}%`}>
              <Tag 
                color={getFailureRateColor(row.aadhaarpayFailureRate)}
                style={{ fontSize: '10px', margin: 0 }}
              >
                {getStatusIcon(row.aadhaarpayFailureRate)} {row.aadhaarpayFailureRate}%
              </Tag>
            </Tooltip>
          )}
        </Space>
      )
    },
    {
      name: "Performance",
      selector: row => row.performanceScore || (100 - (row.aepsFailureRate + row.aadhaarpayFailureRate) / 2),
      sortable: true,
      width: '150px',
      cell: row => {
        const performanceScore = row.performanceScore || (100 - (row.aepsFailureRate + row.aadhaarpayFailureRate) / 2);
        const scoreColor = performanceScore >= 80 ? "success" : 
                          performanceScore >= 60 ? "blue" : 
                          performanceScore >= 40 ? "orange" : "red";
        
        return (
          <Tooltip title={`Overall Performance: ${performanceScore.toFixed(1)}/100`}>
            <div style={{ width: '100%' }}>
              <div style={{ 
                height: 6, 
                backgroundColor: '#f0f0f0',
                borderRadius: 3,
                overflow: 'hidden',
                marginBottom: 4
              }}>
                <div 
                  style={{ 
                    height: '100%',
                    width: `${Math.min(100, performanceScore)}%`,
                    backgroundColor: scoreColor === "success" ? "#52c41a" :
                                   scoreColor === "blue" ? "#1890ff" :
                                   scoreColor === "orange" ? "#faad14" : "#ff4d4f",
                    borderRadius: 3
                  }}
                />
              </div>
              <Text style={{ fontSize: '11px' }}>
                Score: <Text strong style={{ color: scoreColor === "success" ? "#52c41a" :
                                           scoreColor === "blue" ? "#1890ff" :
                                           scoreColor === "orange" ? "#faad14" : "#ff4d4f" }}>
                  {performanceScore.toFixed(1)}
                </Text>
              </Text>
            </div>
          </Tooltip>
        );
      }
    },
    {
      name: "Actions",
      width: '80px',
      cell: row => (
        <Button
          type="link"
          size="small"
          onClick={() => showBankDetails(row)}
          icon={<InfoCircleOutlined />}
          style={{ padding: 0 }}
        >
          Details
        </Button>
      )
    }
  ];

  /**
   * Initialize
   */
  useEffect(() => {
    fetchBanks();
  }, [fetchBanks]);

  /**
   * Apply filters when dependencies change
   */
  useEffect(() => {
    applyFilters(banks, filterStatus);
  }, [banks, filterStatus, sortConfig, failureRateThreshold, applyFilters]);

  /**
   * Handle search input change
   */
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const stats = calculateStats();

  return (
    <div style={{ minHeight: '100vh', background: '#ffffff' }}>
     

      {/* Controls Section */}
      <Card
        bordered={false}
        style={{ borderRadius: 8, marginBottom: 24 }}
      >
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={12}>
            <Input
              ref={searchInputRef}
              placeholder="Search banks by name, IIN, or ID..."
              value={searchTerm}
              onChange={handleSearchChange}
              prefix={<SearchOutlined />}
              allowClear
              size="large"
              style={{ borderRadius: 6 }}
            />
          </Col>
          <Col xs={24} md={12}>
            <Space wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Select
                value={filterStatus}
                onChange={setFilterStatus}
                style={{ width: 200 }}
                placeholder="Filter by status"
                suffixIcon={<FilterOutlined />}
              >
                <Option value="all">All Banks</Option>
                <Option value="aeps_enabled">AEPS Enabled</Option>
                <Option value="aadhaarpay_enabled">Aadhaar Pay Enabled</Option>
                <Option value="both_enabled">Both Enabled</Option>
                <Option value="excellent">Excellent (0% failure)</Option>
                <Option value="low_failure">Low Failure (≤20%)</Option>
                <Option value="high_failure">High Failure</Option>
              </Select>
              
           
            </Space>
          </Col>
        </Row>

      </Card>

      {/* Data Display Section */}
      {loading ? (
        <Card
          bordered={false}
          style={{ borderRadius: 8, textAlign: 'center', padding: '60px 20px' }}
        >
          <Spin size="large" />
          <Text style={{ display: 'block', marginTop: 16, fontSize: 16 }}>
            Loading Instant Pay AEPS Banks...
          </Text>
        </Card>
      ) : filteredBanks.length === 0 ? (
        <Card bordered={false} style={{ borderRadius: 8 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" size={8}>
                <Text type="secondary">
                  {searchTerm || filterStatus !== 'all' ? "No banks match your filters" : "No banks available"}
                </Text>
                {(searchTerm || filterStatus !== 'all') && (
                  <Button type="primary" onClick={resetFilters}>
                    Clear Filters
                  </Button>
                )}
              </Space>
            }
            style={{ padding: '40px 0' }}
          />
        </Card>
      ) : viewMode === 'table' ? (
        <Card
          bordered={false}
          style={{ borderRadius: 8, overflow: 'hidden' }}
          bodyStyle={{ padding: 0 }}
        >
          <div ref={tableRef}>
            <DataTable
              columns={columns}
              data={filteredBanks}
              pagination
              paginationPerPage={20}
              paginationRowsPerPageOptions={[10, 20, 50, 100]}
              paginationComponentOptions={{
                rowsPerPageText: 'Rows per page:',
                rangeSeparatorText: 'of',
                selectAllRowsItem: true,
                selectAllRowsItemText: 'All'
              }}
              highlightOnHover
              pointerOnHover
              onSort={handleSort}
              sortServer
              defaultSortFieldId="name"
              customStyles={{
                headRow: {
                  style: {
                    backgroundColor: '#fafafa',
                    borderBottom: '2px solid #e8e8e8',
                    fontSize: '13px',
                    fontWeight: 600
                  }
                },
                headCells: {
                  style: {
                    padding: '16px 12px',
                    '&:hover': {
                      backgroundColor: '#f0f0f0'
                    }
                  }
                },
                rows: {
                  style: {
                    fontSize: '14px',
                    '&:hover': {
                      backgroundColor: '#fafafa !important'
                    }
                  }
                },
                cells: {
                  style: {
                    padding: '16px 12px'
                  }
                }
              }}
            />
          </div>
          <Divider style={{ margin: 0 }} />
          <div style={{ padding: '16px', textAlign: 'center', background: '#fafafa' }}>
            <Text type="secondary">
              Showing {filteredBanks.length} of {banks.length} banks • 
              Last updated: {new Date().toLocaleTimeString()}
            </Text>
          </div>
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {filteredBanks.map(bank => (
            <Col key={bank.bankId} xs={24} sm={12} md={8} lg={6}>
              <Card
                hoverable
                onClick={() => showBankDetails(bank)}
                style={{ borderRadius: 8, height: '100%', cursor: 'pointer' }}
                bodyStyle={{ padding: '16px' }}
              >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Text strong ellipsis style={{ fontSize: '16px' }}>
                    {bank.name}
                  </Text>
                  
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Space>
                      <Tag color="blue">ID: {bank.bankId}</Tag>
                      <Tag color="cyan">IIN: {bank.iin}</Tag>
                    </Space>
                    
                    <Divider style={{ margin: '8px 0' }} />
                    
                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                      <Space>
                        <Tag color={bank.aepsEnabled ? "success" : "error"}>
                          AEPS: {bank.aepsEnabled ? "✓" : "✗"}
                        </Tag>
                        <Tag color={getFailureRateColor(bank.aepsFailureRate)}>
                          {bank.aepsFailureRate}%
                        </Tag>
                      </Space>
                      
                      <Space>
                        <Tag color={bank.aadhaarpayEnabled ? "success" : "error"}>
                          Aadhaar Pay: {bank.aadhaarpayEnabled ? "✓" : "✗"}
                        </Tag>
                        <Tag color={getFailureRateColor(bank.aadhaarpayFailureRate)}>
                          {bank.aadhaarpayFailureRate}%
                        </Tag>
                      </Space>
                    </Space>
                  </Space>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Bank Details Modal */}
      <Modal
        title={
          <Space>
            <BankOutlined />
            <Text strong>{selectedBank?.name}</Text>
          </Space>
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsModalVisible(false)}>
            Close
          </Button>
        ]}
        width={600}
        >
        {selectedBank && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Row gutter={16}>
              <Col span={12}>
                <Text strong>Bank ID:</Text>
                <Text style={{ display: 'block', marginTop: 4 }}>{selectedBank.bankId}</Text>
              </Col>
              <Col span={12}>
                <Text strong>IIN:</Text>
                <Text style={{ display: 'block', marginTop: 4 }} code>{selectedBank.iin}</Text>
              </Col>
            </Row>
            
            <Divider />
            
            <Card title="AEPS Status" size="small">
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Space>
                  <Tag color={selectedBank.aepsEnabled ? "success" : "error"}>
                    {selectedBank.aepsEnabled ? "Enabled" : "Disabled"}
                  </Tag>
                  <Tag color={getFailureRateColor(selectedBank.aepsFailureRate)}>
                    Failure Rate: {selectedBank.aepsFailureRate}%
                  </Tag>
                </Space>
                <div style={{ 
                  height: 8, 
                  backgroundColor: '#f0f0f0',
                  borderRadius: 4,
                  overflow: 'hidden'
                }}>
                  <div 
                    style={{ 
                      height: '100%',
                      width: `${Math.min(100, selectedBank.aepsFailureRate)}%`,
                      backgroundColor: getFailureRateColor(selectedBank.aepsFailureRate) === "success" ? "#52c41a" :
                                     getFailureRateColor(selectedBank.aepsFailureRate) === "blue" ? "#1890ff" :
                                     getFailureRateColor(selectedBank.aepsFailureRate) === "orange" ? "#faad14" : "#ff4d4f",
                      borderRadius: 4
                    }}
                  />
                </div>
              </Space>
            </Card>
            
            <Card title="Aadhaar Pay Status" size="small">
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Space>
                  <Tag color={selectedBank.aadhaarpayEnabled ? "success" : "error"}>
                    {selectedBank.aadhaarpayEnabled ? "Enabled" : "Disabled"}
                  </Tag>
                  <Tag color={getFailureRateColor(selectedBank.aadhaarpayFailureRate)}>
                    Failure Rate: {selectedBank.aadhaarpayFailureRate}%
                  </Tag>
                </Space>
                <div style={{ 
                  height: 8, 
                  backgroundColor: '#f0f0f0',
                  borderRadius: 4,
                  overflow: 'hidden'
                }}>
                  <div 
                    style={{ 
                      height: '100%',
                      width: `${Math.min(100, selectedBank.aadhaarpayFailureRate)}%`,
                      backgroundColor: getFailureRateColor(selectedBank.aadhaarpayFailureRate) === "success" ? "#52c41a" :
                                     getFailureRateColor(selectedBank.aadhaarpayFailureRate) === "blue" ? "#1890ff" :
                                     getFailureRateColor(selectedBank.aadhaarpayFailureRate) === "orange" ? "#faad14" : "#ff4d4f",
                      borderRadius: 4
                    }}
                  />
                </div>
              </Space>
            </Card>
            
            <Alert
              message="Performance Summary"
              description={
                <Space direction="vertical" size={4}>
                  <Text>
                    Overall performance score: {(100 - (selectedBank.aepsFailureRate + selectedBank.aadhaarpayFailureRate) / 2).toFixed(1)}/100
                  </Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Lower failure rates indicate better performance
                  </Text>
                </Space>
              }
              type="info"
              showIcon
            />
          </Space>
        )}
      </Modal>

    </div>
  );
};

export default InstantPayAEPSBanks;